import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Calendar, ChevronLeft, RefreshCw, Printer, Download, Search, AlertTriangle,
  ChevronRight as ArrowRight,
} from 'lucide-react';
import { vouchersApi, GroupSummaryProfit } from '../services/api';
import { useToast } from '../components/Toast/Toast';
import useReportKeyboardNav from '../hooks/useReportKeyboardNav';
import useDrillPath from '../hooks/useDrillPath';
import useDrillFocus from '../hooks/useDrillFocus';
import useChangePeriodKey from '../hooks/useChangePeriodKey';

/**
 * Profit & Loss A/c in Tally's two-column T-account layout.
 *
 *   Trading account            Dr | Cr
 *     Opening Stock               |  Sales Accounts
 *     Purchase Accounts           |  Direct Incomes
 *     Direct Expenses             |  Closing Stock
 *     [Gross Profit c/o]          |  [Gross Loss c/o]
 *     ------------------- both sides equal -------------------
 *   Profit & Loss account
 *     [Gross Loss b/f]            |  [Gross Profit b/f]
 *     Indirect Expenses           |  Indirect Incomes
 *     [Nett Profit]               |  [Nett Loss]
 *     ------------------- both sides equal -------------------
 *
 * Each line sits on the side its balance actually falls, exactly as Tally does
 * — an expense group carrying a net credit (a reversal, say) moves to the Cr
 * column rather than showing as a negative debit. The balancing figure always
 * goes on the lighter side, which is what makes both totals agree.
 */

const fmt = (n: any) =>
  Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const toInputDate = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const displayDate = (s?: string | null) => {
  if (!s) return '—';
  const d = new Date(s);
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
};
function fyBounds(d: Date) {
  const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return { from: new Date(y, 3, 1), to: new Date(y + 1, 2, 31) };
}

const STORAGE_KEY = 'profit-loss-filters';

/** One side of a section. `italic` marks the balancing figure (Gross/Nett
 *  Profit or Loss), which Tally renders in italics. `nature` is set on the
 *  lines that came from a ledger group and can therefore be drilled. */
type Line = {
  label: string;
  amount: number;
  italic?: boolean;
  nature?: string;
  /** 'stock' lines open the Stock Summary instead of a group breakdown. */
  drill?: 'group' | 'stock';
  /** Position in the keyboard walk over the statement's openable lines. */
  navIndex?: number;
  /** 0 = debit column, 1 = credit column — ← / → move between them. */
  navCol?: 0 | 1;
  /** Visual row this line sits on within its column, counting both sections,
   *  so ← / → can land on the line that's actually alongside. */
  navRow?: number;
};

/** A step in the drill-down: which group we're inside, and the sign convention
 *  inherited from the P&L line that opened it. */
type Crumb = { id: number; name: string; income: boolean; fromIndex?: number };
// fromIndex = the cursor row, at the PARENT level, that opened this crumb.
// It lives on the crumb (and so in the URL) rather than in a ref, because a
// ref is wiped when opening a ledger unmounts the page — which is why coming
// back and then stepping up a level used to land the cursor on the first row.

type DrillRow = {
  type: 'group' | 'ledger';
  id: number;
  name: string;
  debit: number;
  credit: number;
  /** Movement in the parent line's natural direction. */
  net: number;
};

/** Places a signed component on its natural side, flipping when the balance
 *  runs the other way. `naturalDebit` = where the line normally belongs; the
 *  value is "positive means it sits on that side". */
function place(
  dr: Line[], cr: Line[], label: string, value: number, naturalDebit: boolean,
  extra: Pick<Line, 'nature' | 'drill'> = {},
) {
  const line = (amount: number): Line => ({ label, amount, ...extra });
  if (Math.abs(value) <= 0.005) {
    // Tally still prints the caption with a blank amount, so the reader can
    // see the group exists and simply had no movement.
    (naturalDebit ? dr : cr).push(line(0));
    return;
  }
  if (value > 0) (naturalDebit ? dr : cr).push(line(value));
  else (naturalDebit ? cr : dr).push(line(-value));
}

export default function ProfitLoss() {
  const navigate = useNavigate();
  const { showError } = useToast();
  const [searchParams] = useSearchParams();

  const initial = (() => {
    const today = new Date();
    const { from } = fyBounds(today);
    const fallback = { dateFrom: toInputDate(from), dateTo: toInputDate(today) };
    // A period passed in the URL (e.g. from Group Summary) always wins, so the
    // two reports stay on the same dates.
    const urlFrom = searchParams.get('date_from');
    const urlTo = searchParams.get('date_to');
    if (urlFrom || urlTo) {
      return { dateFrom: urlFrom || fallback.dateFrom, dateTo: urlTo || fallback.dateTo };
    }
    try { const s = localStorage.getItem(STORAGE_KEY); if (s) return { ...fallback, ...JSON.parse(s) }; }
    catch { /* ignore */ }
    return fallback;
  })();

  const [dateFrom, setDateFrom] = useState(initial.dateFrom);
  const [dateTo, setDateTo]     = useState(initial.dateTo);
  const [showControls, setShowControls] = useState(false);
  // Alt+F2 — Tally's Change Period: open the date filter, cursor in From.
  useChangePeriodKey(() => setShowControls(true));
  const [loading, setLoading]   = useState(false);
  const [printing, setPrinting] = useState(false);
  const [profit, setProfit]     = useState<GroupSummaryProfit | null>(null);
  const [broughtForward, setBroughtForward] = useState(0);

  // Drill-down: empty = the statement itself, otherwise we're inside a group.
  // Mirrored into the URL so drilling out to a ledger and pressing Back returns
  // to THIS depth instead of the report root.
  const [crumbs, setCrumbs] = useDrillPath<Crumb>();
  const { remember: rememberFocus, take: takeFocus } = useDrillFocus('profit-loss');
  const [drillRows, setDrillRows] = useState<DrillRow[]>([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const current = crumbs.length ? crumbs[crumbs.length - 1] : null;

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ dateFrom, dateTo })); } catch { /* ignore */ }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    const onAfter = () => setPrinting(false);
    window.addEventListener('afterprint', onAfter);
    return () => window.removeEventListener('afterprint', onAfter);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await vouchersApi.getProfitLoss({
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      });
      if (res.success) {
        setProfit(res.data.profit ?? null);
        setBroughtForward(res.data.brought_forward || 0);
      }
    } catch { showError('Error', 'Failed to load the Profit & Loss A/c'); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo, showError]);

  useEffect(() => { load(); }, [load]);

  // Drill-down uses the Group Summary's group listing, but reports the PERIOD
  // MOVEMENT rather than a closing balance — a P&L line is movement, so its
  // breakdown has to be too, or the parts wouldn't sum to the whole.
  useEffect(() => {
    if (!current) { setDrillRows([]); return; }
    let cancelled = false;
    setDrillLoading(true);
    vouchersApi.getGroupLedgers(current.id, {
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    })
      .then(res => {
        if (cancelled || !res.success) return;
        const rows: DrillRow[] = (res.data.rows || []).map((r: any) => {
          const debit = Number(r.debit_total) || 0;
          const credit = Number(r.credit_total) || 0;
          return {
            type: r.row_type === 'subgroup' ? 'group' : 'ledger',
            id: r.row_type === 'subgroup' ? r.group_id : r.ledger_id,
            name: r.ledger_name,
            debit, credit,
            net: current.income ? credit - debit : debit - credit,
          };
        });
        setDrillRows(rows);
      })
      .catch(() => { if (!cancelled) showError('Error', 'Failed to load the breakdown'); })
      .finally(() => { if (!cancelled) setDrillLoading(false); });
    return () => { cancelled = true; };
  }, [current, dateFrom, dateTo, showError]);

  const INCOME_NATURES = ['sales', 'direct_income', 'indirect_income'];

  // Where the cursor should land when stepping back up a level.
  const pendingRestore = useRef<number | null>(null);

  /** Open a P&L line. Stock lines go to the item-wise valuation; group lines
   *  open the group that produced the figure (or, in the unlikely case several
   *  root groups map to one nature, the first — they're summed on the line). */
  const openLine = (line: Line, fromIndex?: number) => {
    // Remember which statement line we left from so Esc brings the cursor back
    // to it. Works for the mouse too, not just Enter.
    if (line.drill === 'stock') {
      const qs = new URLSearchParams();
      if (dateFrom) qs.append('date_from', dateFrom);
      if (dateTo) qs.append('date_to', dateTo);
      navigate(`/reports/stock-summary${qs.toString() ? `?${qs.toString()}` : ''}`);
      return;
    }
    if (line.drill !== 'group' || !line.nature || !profit) return;
    const src = profit.sources.filter(s => s.nature === line.nature);
    if (!src.length) return;
    setCrumbs([{ id: src[0].group_id, name: src[0].group_name, income: INCOME_NATURES.includes(line.nature), fromIndex }]);
  };

  const drillTotal = drillRows.reduce((s, r) => s + r.net, 0);

  // ── Build both sections ──
  const tradingDr: Line[] = [];
  const tradingCr: Line[] = [];
  const plDr: Line[] = [];
  const plCr: Line[] = [];
  let tradingTotal = 0, plTotal = 0;

  if (profit) {
    place(tradingDr, tradingCr, 'Opening Stock', profit.opening_stock, true, { drill: 'stock' });
    place(tradingDr, tradingCr, 'Purchase Accounts', profit.purchases, true, { nature: 'purchase', drill: 'group' });
    place(tradingDr, tradingCr, 'Direct Expenses', profit.direct_expenses, true, { nature: 'direct_expense', drill: 'group' });
    place(tradingDr, tradingCr, 'Sales Accounts', profit.sales, false, { nature: 'sales', drill: 'group' });
    place(tradingDr, tradingCr, 'Direct Incomes', profit.direct_incomes, false, { nature: 'direct_income', drill: 'group' });
    place(tradingDr, tradingCr, 'Closing Stock', profit.closing_stock, false, { drill: 'stock' });

    // The balancing figure goes on the lighter side so both columns agree.
    if (profit.gross_profit >= 0) {
      tradingDr.push({ label: 'Gross Profit c/o', amount: profit.gross_profit, italic: true });
    } else {
      tradingCr.push({ label: 'Gross Loss c/o', amount: -profit.gross_profit, italic: true });
    }
    tradingTotal = tradingDr.reduce((s, l) => s + l.amount, 0);

    // Carried into the P&L section on the opposite side to where it closed.
    if (profit.gross_profit >= 0) {
      plCr.push({ label: 'Gross Profit b/f', amount: profit.gross_profit, italic: true });
    } else {
      plDr.push({ label: 'Gross Loss b/f', amount: -profit.gross_profit, italic: true });
    }
    place(plDr, plCr, 'Indirect Expenses', profit.indirect_expenses, true, { nature: 'indirect_expense', drill: 'group' });
    place(plDr, plCr, 'Indirect Incomes', profit.indirect_incomes, false, { nature: 'indirect_income', drill: 'group' });

    if (profit.nett_profit >= 0) {
      plDr.push({ label: 'Nett Profit', amount: profit.nett_profit, italic: true });
    } else {
      plCr.push({ label: 'Nett Loss', amount: -profit.nett_profit, italic: true });
    }
    plTotal = plDr.reduce((s, l) => s + l.amount, 0);
  }

  // The statement is a T-account, not a row list, so number its openable lines
  // down the left column and then the right — that's the order ↑/↓ walk them
  // in. Each also records the column and the visual row it occupies, so ← / →
  // can jump across to whatever sits alongside. Balancing figures are skipped;
  // there's nothing behind them to open.
  const navLines: Line[] = [];
  const tradingRows = Math.max(tradingDr.length, tradingCr.length);
  const sides: Array<{ arr: Line[]; col: 0 | 1; offset: number }> = [
    { arr: tradingDr, col: 0, offset: 0 },
    { arr: tradingCr, col: 1, offset: 0 },
    { arr: plDr, col: 0, offset: tradingRows + 1 },
    { arr: plCr, col: 1, offset: tradingRows + 1 },
  ];
  for (const { arr, col, offset } of sides) {
    arr.forEach((l, i) => {
      if (!l.drill) return;
      l.navIndex = navLines.length;
      l.navCol = col;
      l.navRow = offset + i;
      navLines.push(l);
    });
  }

  const goDeeper = (fromIndex: number, crumb: Crumb) => {
    setCrumbs(prev => [...prev, { ...crumb, fromIndex }]);
  };
  const goBack = () => {
    if (!crumbs.length) return;
    pendingRestore.current = crumbs[crumbs.length - 1]?.fromIndex ?? 0;
    setCrumbs(prev => prev.slice(0, -1));
  };

  // Opening a ledger leaves this route entirely, so note which row we left from
  // — coming back restores the depth (via the URL) AND lands the cursor here.
  const openLedger = (id: number, index: number) => {
    rememberFocus(JSON.stringify(crumbs), id, index);
    navigate(`/reports/ledger?ledger_id=${id}`);
  };

  // Keyboard navigation — over the drill rows when drilled in, otherwise over
  // the statement's openable lines.
  const scrollRef = useRef<HTMLDivElement>(null);
  // Mirrors the cursor so onHorizontal can read it — it's defined as part of
  // the same statement that produces activeIndex, so it can't close over it.
  const activeRef = useRef(-1);
  const { activeIndex, focusIndex, rowProps, rowClass } = useReportKeyboardNav({
    rowCount: current ? drillRows.length : navLines.length,
    containerRef: scrollRef,
    enabled: !showControls && !printing,
    onActivate: i => {
      if (!current) { const l = navLines[i]; if (l) openLine(l, i); return; }
      const r = drillRows[i];
      if (!r) return;
      if (r.type === 'group') goDeeper(i, { id: r.id, name: r.name, income: current.income });
      else openLedger(r.id, i);
    },
    onBack: goBack,
    // On the statement, ← / → hop between the debit and credit columns,
    // landing on whichever openable line is nearest the current row.
    onHorizontal: dir => {
      if (current) return false;             // drill view: let it scroll instead
      const from = navLines[activeRef.current];
      if (!from) { if (navLines.length) focusIndex(0); return true; }
      const targetCol = dir < 0 ? 0 : 1;
      if (from.navCol === targetCol) return true;   // already there — absorb the key
      const candidates = navLines.filter(l => l.navCol === targetCol);
      if (!candidates.length) return true;
      const best = candidates.reduce((a, b) =>
        Math.abs((b.navRow ?? 0) - (from.navRow ?? 0)) < Math.abs((a.navRow ?? 0) - (from.navRow ?? 0)) ? b : a);
      focusIndex(best.navIndex!);
      return true;
    },
  });

  useEffect(() => { activeRef.current = activeIndex; }, [activeIndex]);

  // Put the cursor back once the level we returned to has its rows. Two sources:
  // an in-page step back up the breadcrumb (pendingRestore), or a return from
  // the ledger route (the focus memo). The memo is matched by ledger ID first
  // so the cursor lands on the same ledger even if the rows have since shifted;
  // the saved index is only the fallback for a row that no longer exists.
  useEffect(() => {
    const count = current ? drillRows.length : navLines.length;
    if (count <= 0) return;

    if (pendingRestore.current === null) {
      const memo = takeFocus(JSON.stringify(crumbs));
      if (memo) {
        const byId = drillRows.findIndex(r => r.type === 'ledger' && r.id === memo.id);
        focusIndex(byId >= 0 ? byId : Math.min(memo.index, count - 1));
      }
      return;
    }

    const target = Math.min(pendingRestore.current, count - 1);
    pendingRestore.current = null;
    focusIndex(target);
  }, [current, drillRows, navLines.length, focusIndex, crumbs, takeFocus]);

  const handlePrint = () => { setPrinting(true); setTimeout(() => window.print(), 50); };

  const handleExcel = () => {
    if (!profit) return;
    const rows: string[][] = [
      ['Profit & Loss A/c', `${displayDate(dateFrom)} to ${displayDate(dateTo)}`],
      [],
      ['Particulars', 'Debit', 'Particulars', 'Credit'],
    ];
    const pair = (dr: Line[], cr: Line[], total: number) => {
      for (let i = 0; i < Math.max(dr.length, cr.length); i++) {
        rows.push([
          dr[i]?.label || '', dr[i] && dr[i].amount ? fmt(dr[i].amount) : '',
          cr[i]?.label || '', cr[i] && cr[i].amount ? fmt(cr[i].amount) : '',
        ]);
      }
      rows.push(['', fmt(total), '', fmt(total)]);
    };
    pair(tradingDr, tradingCr, tradingTotal);
    rows.push([]);
    pair(plDr, plCr, plTotal);
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Profit_and_Loss_${dateFrom}_to_${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const cellL = 'px-3 py-1.5 text-[13px] leading-snug';
  const cellR = 'px-3 py-1.5 text-[13px] leading-snug text-right tabular-nums';

  /** One column of a section, padded so both sides line up row-for-row. */
  const Column = ({ lines, rows }: { lines: Line[]; rows: number }) => (
    <table className="w-full border-collapse">
      <tbody>
        {Array.from({ length: rows }).map((_, i) => {
          const l = lines[i];
          const clickable = !!l?.drill && !printing;
          const nav = l?.navIndex;
          return (
            <tr key={i}
              {...(nav !== undefined ? rowProps(nav) : {})}
              onClick={clickable ? () => openLine(l, l.navIndex) : undefined}
              title={clickable ? (l.drill === 'stock' ? 'Open the Stock Summary' : `Break down ${l.label}`) : undefined}
              className={`${clickable ? 'cursor-pointer hover:bg-blue-50 transition-colors' : ''} ${nav !== undefined ? rowClass(nav) : ''}`}>
              <td className={`${cellL} ${l?.italic ? 'italic font-semibold text-slate-800' : 'font-semibold text-slate-700'} ${clickable ? 'text-blue-700' : ''}`}>
                {l?.label || ' '}
              </td>
              <td className={`${cellR} ${l?.italic ? 'italic font-semibold text-slate-800' : 'text-slate-700'}`}>
                {l && Math.abs(l.amount) > 0.005 ? fmt(l.amount) : ''}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const Section = ({ dr, cr, total }: { dr: Line[]; cr: Line[]; total: number }) => {
    const rows = Math.max(dr.length, cr.length);
    return (
      <div className="grid grid-cols-2 divide-x divide-slate-300">
        <div>
          <Column lines={dr} rows={rows} />
          <div className="border-t-2 border-slate-400 mx-3" />
          <table className="w-full"><tbody><tr>
            <td className={`${cellL} font-bold text-slate-800`}>&nbsp;</td>
            <td className={`${cellR} font-bold text-slate-900`}>{fmt(total)}</td>
          </tr></tbody></table>
        </div>
        <div>
          <Column lines={cr} rows={rows} />
          <div className="border-t-2 border-slate-400 mx-3" />
          <table className="w-full"><tbody><tr>
            <td className={`${cellL} font-bold text-slate-800`}>&nbsp;</td>
            <td className={`${cellR} font-bold text-slate-900`}>{fmt(total)}</td>
          </tr></tbody></table>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col w-full fixed left-0 right-0 top-14 bottom-16 sm:static sm:h-full" style={{ overscrollBehavior: 'contain' }}>

      {/* Header */}
      <div className="flex-none bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between print:hidden">
        <div className="flex items-center gap-1 min-w-0 flex-1">
          <button
            onClick={() => crumbs.length ? goBack() : navigate(-1)}
            className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 -ml-1 flex-shrink-0">
            <ChevronLeft size={20} />
          </button>
          {/* Breadcrumb — the statement, then each group drilled into. */}
          <div className="flex items-center gap-1 min-w-0 flex-wrap">
            <button
              onClick={() => { if (crumbs.length) { pendingRestore.current = crumbs[0]?.fromIndex ?? 0; setCrumbs([]); } }}
              className={`text-[15px] font-bold truncate ${crumbs.length ? 'text-blue-600 hover:underline' : 'text-slate-800'}`}>
              Profit &amp; Loss A/c
            </button>
            {crumbs.map((c, i) => (
              <span key={`${c.id}-${i}`} className="flex items-center gap-1 min-w-0">
                <ArrowRight size={13} className="text-slate-400 flex-shrink-0" />
                <button
                  onClick={() => setCrumbs(prev => prev.slice(0, i + 1))}
                  className={`text-[14px] font-semibold truncate max-w-[160px] ${
                    i === crumbs.length - 1 ? 'text-slate-800' : 'text-blue-600 hover:underline'
                  }`}>
                  {c.name}
                </button>
              </span>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={() => setShowControls(v => !v)}
            title="Period & filters (Alt+F2)"
            className={`p-2 rounded-lg transition-colors ${showControls ? 'bg-blue-600 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>
            <Search size={18} />
          </button>
          <button onClick={handleExcel} disabled={!profit} title="Export to Excel"
            className="p-2 rounded-lg text-green-600 hover:bg-green-50 disabled:opacity-40">
            <Download size={16} />
          </button>
          <button onClick={handlePrint} disabled={!profit}
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-40">
            <Printer size={16} />
          </button>
          <button onClick={load} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Filters */}
      {showControls && (
        <div className="flex-none bg-slate-50 border-b border-slate-200 px-3 py-2.5 print:hidden">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-stretch border border-slate-300 rounded-lg bg-white overflow-hidden">
              <div className="flex items-center gap-1 px-2 border-r border-slate-300 bg-slate-100">
                <Calendar size={12} className="text-slate-400" />
                <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wide">From</span>
              </div>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                className="text-[13px] text-slate-700 outline-none bg-transparent px-2 py-1.5 w-[115px]" />
              <div className="flex items-center px-2 border-l border-r border-slate-300 bg-slate-100">
                <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wide">To</span>
              </div>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                className="text-[13px] text-slate-700 outline-none bg-transparent px-2 py-1.5 w-[115px]" />
            </div>
          </div>
        </div>
      )}

      {/* ── Drill-down: what makes up one P&L line ── */}
      {current ? (
        <div ref={scrollRef} className="flex-1 min-h-0 overflow-auto bg-white">
          <table className="w-full border-collapse">
            <thead className="sticky top-0 z-10">
              <tr>
                <th className="border border-slate-400 bg-slate-200 px-2.5 py-1.5 text-[13px] font-bold text-slate-700 uppercase tracking-wide text-left">
                  Particulars
                </th>
                <th className="border border-slate-400 bg-slate-200 px-2.5 py-1.5 text-[13px] font-bold text-slate-700 uppercase tracking-wide text-right w-[150px]">Debit</th>
                <th className="border border-slate-400 bg-slate-200 px-2.5 py-1.5 text-[13px] font-bold text-slate-700 uppercase tracking-wide text-right w-[150px]">Credit</th>
                <th className="border border-slate-400 bg-slate-200 px-2.5 py-1.5 text-[13px] font-bold text-slate-700 uppercase tracking-wide text-right w-[160px]">
                  {current.income ? 'Net Income' : 'Net Expense'}
                </th>
              </tr>
            </thead>
            <tbody>
              {drillLoading ? (
                <tr><td colSpan={4} className="text-center text-slate-400 py-16 text-[14px]">Loading…</td></tr>
              ) : drillRows.length === 0 ? (
                <tr><td colSpan={4} className="text-center text-slate-400 py-16 text-[14px]">
                  No movement in {current.name} for {displayDate(dateFrom)} – {displayDate(dateTo)}
                </td></tr>
              ) : drillRows.map((row, rowIdx) => {
                const isGroup = row.type === 'group';
                return (
                  <tr key={`${row.type}-${row.id}`}
                    {...rowProps(rowIdx)}
                    onClick={() => isGroup
                      ? goDeeper(rowIdx, { id: row.id, name: row.name, income: current.income })
                      : openLedger(row.id, rowIdx)}
                    title={isGroup ? `Break down ${row.name}` : `Open the ${row.name} ledger`}
                    className={`bg-white hover:bg-blue-50 cursor-pointer transition-colors ${rowClass(rowIdx)}`}>
                    <td className="border border-slate-300 px-2.5 py-1.5 text-[13px]">
                      <div className="flex items-center gap-2">
                        {isGroup
                          ? <ArrowRight size={14} className="text-blue-400 flex-shrink-0" />
                          : <span className="w-3 h-3 rounded-full border border-slate-300 flex-shrink-0" />}
                        <span className={isGroup ? 'font-semibold text-slate-800' : 'text-slate-700'}>{row.name}</span>
                      </div>
                    </td>
                    <td className="border border-slate-300 px-2.5 py-1.5 text-[13px] text-right tabular-nums text-emerald-700">
                      {row.debit > 0.005 ? fmt(row.debit) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="border border-slate-300 px-2.5 py-1.5 text-[13px] text-right tabular-nums text-red-600">
                      {row.credit > 0.005 ? fmt(row.credit) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className={`border border-slate-300 px-2.5 py-1.5 text-[13px] text-right tabular-nums font-semibold ${
                      row.net >= 0 ? 'text-slate-800' : 'text-red-600'}`}>
                      {Math.abs(row.net) > 0.005 ? fmt(row.net) : <span className="text-slate-300">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {!drillLoading && drillRows.length > 0 && (
              <tfoot>
                <tr className="bg-slate-200 font-bold sticky bottom-0">
                  <td className="border border-slate-400 px-2.5 py-1.5 text-[13px] text-slate-700">
                    Total — {current.name}
                  </td>
                  <td className="border border-slate-400 px-2.5 py-1.5 text-right text-[13px] tabular-nums text-emerald-700">
                    {fmt(drillRows.reduce((s, r) => s + r.debit, 0))}
                  </td>
                  <td className="border border-slate-400 px-2.5 py-1.5 text-right text-[13px] tabular-nums text-red-600">
                    {fmt(drillRows.reduce((s, r) => s + r.credit, 0))}
                  </td>
                  <td className="border border-slate-400 px-2.5 py-1.5 text-right text-[13px] tabular-nums text-slate-900">
                    {fmt(drillTotal)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
          <div className="px-4 py-3 text-[11px] text-slate-400">
            Movement for {displayDate(dateFrom)} – {displayDate(dateTo)} — the same basis as the
            statement, so these rows add up to the {current.name} line on it. Click a ledger to open
            its voucher-level report.
          </div>
        </div>
      ) : (
      // Same ref as the drill view — only one branch is mounted at a time, and
      // the statement needs it so the arrow keys can scroll it.
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-auto bg-white">
        {loading ? (
          <div className="py-20 text-center text-slate-400 text-sm">Loading…</div>
        ) : !profit ? (
          <div className="py-20 text-center text-slate-400 text-sm px-8">
            The Profit &amp; Loss A/c is a company-wide statement, so it isn't available to users
            restricted to a ledger group.
          </div>
        ) : (
          <div className="border border-slate-300 m-3 rounded-lg overflow-hidden">
            {/* Column headings */}
            <div className="grid grid-cols-2 divide-x divide-slate-300 bg-slate-100 border-b border-slate-300">
              {['Particulars', 'Particulars'].map((h, i) => (
                <div key={i} className="flex items-baseline justify-between px-3 py-2">
                  <span className="text-[13px] font-bold tracking-[0.2em] text-slate-700">{h}</span>
                  <span className="text-[11px] text-slate-500 text-right">
                    {displayDate(dateFrom)} to {displayDate(dateTo)}
                  </span>
                </div>
              ))}
            </div>

            <Section dr={tradingDr} cr={tradingCr} total={tradingTotal} />
            <div className="h-3 bg-slate-50 border-y border-slate-200" />
            <Section dr={plDr} cr={plCr} total={plTotal} />

            {/* Grand total — both sides of the P&L section */}
            <div className="grid grid-cols-2 divide-x divide-slate-300 bg-slate-200 border-t border-slate-400">
              {[0, 1].map(i => (
                <div key={i} className="flex items-center justify-between px-3 py-2">
                  <span className="text-[13px] font-bold tracking-[0.15em] text-slate-800">Total</span>
                  <span className="text-[13px] font-bold tabular-nums text-slate-900">{fmt(plTotal)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {profit && (
          <div className="px-4 pb-4 space-y-2 print:hidden">
            {Math.abs(broughtForward) > 0.005 && (
              <div className="text-[11px] text-slate-500">
                This statement covers {displayDate(dateFrom)} – {displayDate(dateTo)} only. The
                Profit &amp; Loss A/c line on the Group Summary is cumulative and differs by{' '}
                <strong>{fmt(Math.abs(broughtForward))}</strong> — the prior-period balance still
                sitting on nominal accounts, net of opening stock.
              </div>
            )}
            {profit.unclassified.length > 0 && (
              <div className="flex items-start gap-1.5 text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <span>
                  Not counted — these top-level groups don't match a Tally primary group name:{' '}
                  <strong>{profit.unclassified.join(', ')}</strong>. Rename them to the standard names
                  (Sales Accounts, Purchase Accounts, Direct/Indirect Incomes, Direct/Indirect
                  Expenses) to include them.
                </span>
              </div>
            )}
          </div>
        )}
      </div>
      )}
    </div>
  );
}
